// SendEmailActionRow.jsx

import React from 'react';

// Utilities
import {
  EMAIL_RECIPIENT_SOURCE_OPTIONS,
  normalizeRecipientSource,
} from '../utils/automationActionEditorUtils';
import { getNextIncludedFieldKeys } from '../utils/automationEmailActionUtils';

// Hooks
import useSendEmailActionState from '../hooks/useSendEmailActionState';

// CSS
import './SendEmailActionRow.css';

// Components
import SendEmailActionHeader from './SendEmailActionHeader';
import SendEmailIncludedFieldsSection from './SendEmailIncludedFieldsSection';
import SendEmailMessageSection from './SendEmailMessageSection';
import SendEmailRecipientsSection from './SendEmailRecipientsSection';

/**
 * SendEmailActionRow
 *
 * Renders one email action row and its recipient/message editors.
 *
 * @param {object} action - Action row payload.
 * @param {object} theme - Theme object used for dynamic styling.
 * @param {array} actions - Full action list for remove-button state.
 * @param {array} fieldOptions - Available fields for include-information selection.
 * @param {array} nodeTypeOptions - Base node type options with people fields.
 * @param {boolean} [allowEmailActions=true] - Whether send-email actions can be selected.
 * @param {function} updateAction - Action row updater callback.
 * @param {function} onTypeChange - Callback to switch between action types.
 * @param {function} onRemove - Callback to remove this action row.
 * @param {function} onOpenRecipients - Callback to open recipient picker.
 * @param {array} [selectedRecipients=[]] - Resolved explicit-member recipient profiles.
 *
 * @returns {ReactNode} Rendered send-email row.
 */
const SendEmailActionRow = React.memo(({
  action,
  theme,
  actions,
  fieldOptions,
  nodeTypeOptions,
  allowEmailActions = true,
  selectedRecipients = [],
  updateAction,
  onTypeChange,
  onRemove,
  onOpenRecipients,
}) => {

  // ----- HOOKS ----- //

  const {
    recipientSource,
    messageConfig,
    emailBulletFieldOptions,
    selectableFieldKeySet,
    effectiveIncludedFieldKeys,
    currentPeopleFieldOptions,
    relatedPeopleFieldOptions,
  } = useSendEmailActionState({
    action,
    fieldOptions,
    nodeTypeOptions,
  });

  // ----- HANDLERS ----- //

  /**
   * Updates the recipient source mode for the current email action.
   *
   * @param {string} nextType - Next recipient source type.
   */

  const handleRecipientSourceTypeChange = (nextType) => {
    updateAction((entry) => {
      const existingSource = normalizeRecipientSource(entry);
      return {
        ...entry,
        recipientSource: {
          ...existingSource,
          type: EMAIL_RECIPIENT_SOURCE_OPTIONS.some((option) => option.value === nextType)
            ? nextType
            : 'explicit_members',
        },
      };
    });
  };

  /**
   * Toggles a field in the include-information selection list.
   *
   * @param {string} fieldKey - Target field key.
   */
  const handleToggleIncludedField = (fieldKey) => {
    updateAction((entry) => {
      const nextIncludedFieldKeys = getNextIncludedFieldKeys({
        currentFieldKeys: entry?.message?.includedFieldKeys,
        fieldKey,
        selectableFieldKeySet,
      });

      return {
        ...entry,
        message: {
          ...(entry?.message || {}),
          includedFieldKeys: nextIncludedFieldKeys,
        },
      };
    });
  };

  // ----- RENDER ----- //

  return (
    <div className="send-email-action-row-container" key={action.key}>

      <SendEmailActionHeader
        theme={theme}
        canRemove={actions.length > 1}
        allowEmailActions={allowEmailActions}
        recipientSourceType={recipientSource.type}
        onActionTypeChange={onTypeChange}
        onRecipientSourceTypeChange={handleRecipientSourceTypeChange}
        onRemove={onRemove}
      />

      <SendEmailRecipientsSection
        theme={theme}
        recipientSource={recipientSource}
        selectedRecipients={selectedRecipients}
        currentPeopleFieldOptions={currentPeopleFieldOptions}
        nodeTypeOptions={nodeTypeOptions}
        relatedPeopleFieldOptions={relatedPeopleFieldOptions}
        onOpenRecipients={onOpenRecipients}
        onRelatedNodeTypeChange={(nextTypeKey) => {
          updateAction((entry) => {
            const existingSource = normalizeRecipientSource(entry);
            return {
              ...entry,
              recipientSource: {
                ...existingSource,
                type: 'related_node_people_field',
                relatedNodeTypeKey: nextTypeKey,
                relatedPeopleFieldKey: '',
              },
            };
          });
        }}
        onRelatedPeopleFieldChange={(nextFieldKey) => {
          updateAction((entry) => {
            const existingSource = normalizeRecipientSource(entry);
            return {
              ...entry,
              recipientSource: {
                ...existingSource,
                type: 'related_node_people_field',
                relatedPeopleFieldKey: nextFieldKey,
              },
            };
          });
        }}
        onTriggerPeopleFieldChange={(nextFieldKey) => {
          updateAction((entry) => {
            const existingSource = normalizeRecipientSource(entry);
            return {
              ...entry,
              recipientSource: {
                ...existingSource,
                type: 'trigger_people_field',
                triggerPeopleFieldKey: nextFieldKey,
              },
            };
          });
        }}
      />

      <SendEmailMessageSection
        messageText={messageConfig.text}
        onMessageChange={(nextText) => {
          updateAction((entry) => ({
            ...entry,
            message: {
              ...(entry?.message || {}),
              text: nextText,
            },
          }));
        }}
      />

      <SendEmailIncludedFieldsSection
        theme={theme}
        emailBulletFieldOptions={emailBulletFieldOptions}
        effectiveIncludedFieldKeys={effectiveIncludedFieldKeys}
        onToggleField={handleToggleIncludedField}
      />

    </div>
  );
});

// Dev Tools
SendEmailActionRow.displayName = 'SendEmailActionRow';

export default SendEmailActionRow;
